import {
  ReactElement,
  ReactNode,
  FC,
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { useWindowSize } from 'react-use'
import { Paper, SvgIconProps, SxProps, Theme, TooltipProps } from '@mui/material'
import ToggleButton from '../ToggleButtonGroup'

export interface ToggleButtonDropDownHandle {
  close: () => void
}

interface Props {
  hint?: string
  children: ReactNode
  Icon: FC<SvgIconProps>
  style?: SxProps<Theme>
  iconSize?: 'small' | 'inherit' | 'medium' | 'large'
  extendTo?: 'right' | 'left'
  /** When true, the menu opens above the control (e.g. bottom-docked toolbars). */
  openAbove?: boolean
  /** Tooltip for the main button; use with `tooltipPlacement` for bottom-docked toolbars. */
  tooltipPlacement?: TooltipProps['placement']
  onClick?: () => void
  'data-cy'?: string
  toggleType?: 'hover' | 'click'
  iconColour?: 'primary' | 'secondary' | 'inherit'
  selected?: boolean
  disabled?: boolean
  closeOnChildrenClick?: boolean
}

const ToggleButtonDropDown = forwardRef<ToggleButtonDropDownHandle, Props>(
  (
    {
      hint,
      children,
      Icon,
      style,
      iconSize,
      extendTo = 'left',
      toggleType = 'click',
      iconColour = 'inherit',
      onClick = () => null,
      'data-cy': dataCy,
      selected = false,
      disabled = false,
      closeOnChildrenClick = false,
      openAbove = false,
      tooltipPlacement = 'bottom',
    },
    ref,
  ): ReactElement => {
    const [open, setOpen] = useState(false)
    const targetRef = useRef<unknown>(null)

    const { height } = useWindowSize()

    useImperativeHandle(ref, () => ({
      close: () => setOpen(false),
    }))

    useEffect(() => {
      const close = (event: MouseEvent) => {
        if (event.target !== targetRef.current) {
          setOpen(false)
        }
      }

      document.addEventListener('click', close)
      return () => document.removeEventListener('click', close)
    }, [])

    return (
      <ToggleButton
        selected={selected}
        disabled={disabled}
        sx={{
          position: 'relative',
          overflow: 'visible',
          '&:hover': {
            '.controlsContainer': {
              display: toggleType === 'hover' ? 'block' : undefined,
            },
          },
          ...style,
        }}
        Icon={Icon}
        iconSize={iconSize}
        // as the dropdown contains buttons itself, the parent element cannot be
        // a button. we basically just want the style of a ToggleButton but none
        // of its functionality
        // @ts-ignore
        component="div"
        hint={open ? undefined : hint}
        tooltipPlacement={tooltipPlacement}
        disableRipple
        onClick={event => {
          targetRef.current = event.target
          setOpen(!open)
          onClick()
        }}
        data-cy={dataCy}
        iconColour={iconColour}
      >
        <Paper
          className="controlsContainer"
          elevation={0}
          sx={theme => ({
            display: toggleType === 'hover' ? 'none' : open ? 'block' : 'none',
            position: 'absolute',
            right: extendTo === 'left' ? 0 : undefined,
            left: extendTo === 'right' ? 0 : undefined,
            ...(openAbove
              ? { top: 'auto', bottom: theme.spacing(5) }
              : { top: theme.spacing(5), bottom: 'auto' }),
            p: 1.25,
            zIndex: 1400,
            overflowY: 'auto',
            maxHeight: openAbove ? 'min(50vh, 400px)' : `${height * 0.8}px`,
            borderRadius: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.common.white,
            boxShadow:
              '0 12px 32px rgba(0, 0, 0, 0.10), 0 4px 10px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
            '& .MuiDivider-root': {
              my: 0.5,
            },
          })}
          onClickCapture={() => {
            if (closeOnChildrenClick) {
              setOpen(false)
            }
          }}
          onClick={e => {
            e.stopPropagation()
          }}
        >
          {children}
        </Paper>
      </ToggleButton>
    )
  },
)

ToggleButtonDropDown.displayName = 'ToggleButtonDropDown'

export default ToggleButtonDropDown
